import React, { useEffect, useMemo, useState } from "react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import axiosInstance from "../api/axiosInstance";
import { toast } from "react-toastify";

const ShareDialog = ({ onClose }) => {
  const [phone, setPhone] = useState("");
  const [country, setCountry] = useState(null);
  const [phoneError, setPhoneError] = useState("");

  const [otpDigits, setOtpDigits] = useState(["", "", "", "", "", ""]);
  const [otpSent, setOtpSent] = useState(false);
  const [isOtpVerified, setIsOtpVerified] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const [otpTimer, setOtpTimer] = useState(0);

  const [loadingSend, setLoadingSend] = useState(false);
  const [loadingVerify, setLoadingVerify] = useState(false);
  const [loadingResend, setLoadingResend] = useState(false);
  const [loadingSubmit, setLoadingSubmit] = useState(false);

  const [otpError, setOtpError] = useState("");

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [selfPassword, setSelfPassword] = useState("");

  const [usernameError, setUsernameError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  const [selfPasswordError, setSelfPasswordError] = useState("");

  const basicAuthHeaders = useMemo(
    () => ({
      authorization: "Basic YXBwbGljYXRpb246c2VjcmV0",
      "content-type": "application/json",
      accept: "application/json, text/plain, */*",
    }),
    [],
  );

  const resetForm = () => {
    setPhone("");
    setCountry(null);

    setOtpDigits(["", "", "", "", "", ""]);
    setOtpSent(false);
    setIsOtpVerified(false);
    setOrderId(null);
    setOtpTimer(0);
    setOtpError("");

    setUsername("");
    setPassword("");
    setConfirmPassword("");
    setSelfPassword("");

    setUsernameError("");
    setPasswordError("");
    setConfirmPasswordError("");
    setSelfPasswordError("");

    setLoadingSend(false);
    setLoadingVerify(false);
    setLoadingResend(false);
    setLoadingSubmit(false);
  };

  useEffect(() => {
    if (!otpTimer) return;
    const interval = setInterval(() => {
      setOtpTimer((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [otpTimer]);

  const getPhonePayload = () => {
    const dialCode = country?.dialCode?.toString() || "";
    const mobileWithoutCode =
      phone && dialCode ? phone.toString().slice(dialCode.length) : phone;

    return {
      country_code: dialCode ? `+${dialCode}` : "",
      mobile: mobileWithoutCode,
    };
  };

  const { mobile } = getPhonePayload();
  const canSendOtp =
    !!country?.dialCode &&
    !!mobile &&
    !loadingSend &&
    !isOtpVerified &&
    !otpSent;

  const handleSendOtp = async () => {
    setPhoneError("");
    setOtpError("");
    setIsOtpVerified(false);

    if (!phone || !country?.dialCode) {
      setPhoneError("Please enter a valid mobile number.");
      return;
    }

    const payload = getPhonePayload();

    try {
      setLoadingSend(true);
      const response = await axiosInstance.post(
        "/whatsapp/send-code",
        payload,
        { headers: basicAuthHeaders },
      );

      if (response?.status) {
        const newOrderId = response?.data?.orderId || null;
        setOrderId(newOrderId);

        const expiry = response?.data?.otp_expiry_time ?? 60;
        setOtpTimer(expiry);
        setOtpSent(true);
        setOtpDigits(["", "", "", "", "", ""]);
        setOtpError("");
        toast.success(
          response?.msg ||
            "OTP has been successfully sent to your WhatsApp number.",
        );
      } else {
        setPhoneError(
          response?.msg ||
            "Something went wrong while sending OTP. Please try again.",
        );
      }
    } catch (err) {
      setPhoneError(
        err?.response?.data?.msg ||
          err?.response?.data?.message ||
          "Something went wrong while sending OTP. Please try again.",
      );
      toast.error(
        err?.response?.data?.msg ||
          err?.response?.data?.message ||
          "Something went wrong while sending OTP. Please try again.",
      );
    } finally {
      setLoadingSend(false);
    }
  };

  const handleResendOtp = async () => {
    setOtpError("");

    if (!orderId) {
      setOtpError("Cannot resend OTP. Please try sending a new OTP.");
      return;
    }

    const payload = { orderId };

    try {
      setLoadingResend(true);
      const response = await axiosInstance.post("/whatsapp/re-send", payload, {
        headers: basicAuthHeaders,
      });

      if (response?.status) {
        const expiry = response?.data?.otp_expiry_time ?? 60;
        setOtpTimer(expiry);
        setOtpError("");
        toast.success(
          response?.msg || "OTP has been resent to your WhatsApp number.",
        );
      } else {
        setOtpError(
          response?.msg ||
            "Something went wrong while resending OTP. Please try again.",
        );
      }
    } catch (err) {
      setOtpError(
        err?.response?.data?.msg ||
          err?.response?.data?.message ||
          "Something went wrong while resending OTP. Please try again.",
      );
    } finally {
      setLoadingResend(false);
    }
  };

  const handleVerifyOtp = async () => {
    setOtpError("");

    const code = otpDigits.join("").trim();
    if (code.length !== 6) {
      setOtpError("Please enter the 6 digit OTP.");
      return;
    }

    if (!orderId) {
      setOtpError("Missing order information. Please send OTP again.");
      return;
    }

    const payload = { orderId, otp: code };

    try {
      setLoadingVerify(true);
      const response = await axiosInstance.post("/whatsapp/verify", payload, {
        headers: basicAuthHeaders,
      });

      if (response?.status) {
        setIsOtpVerified(true);
        toast.success(
          response?.msg || "OTP verified successfully. You can continue.",
        );
        setOtpError("");
      } else {
        setIsOtpVerified(false);
        setOtpError(
          response?.msg ||
            "OTP verification failed. Please check the code and try again.",
        );
        toast.error(
          response?.msg ||
            "OTP verification failed. Please check the code and try again.",
        );
      }
    } catch (err) {
      setIsOtpVerified(false);
      setOtpError(
        err?.response?.data?.msg ||
          err?.response?.data?.message ||
          "OTP verification failed. Please try again.",
      );
    } finally {
      setLoadingVerify(false);
    }
  };

  const handleOtpInputChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;
    const newOtp = [...otpDigits];
    newOtp[index] = value;
    setOtpDigits(newOtp);

    if (value && index < otpDigits.length - 1) {
      const nextInput = document.getElementById(`otp-input-${index + 1}`);
      nextInput && nextInput.focus();
    }
  };

  const handleOtpKeyDown = (index, event) => {
    if (event.key === "Backspace" && !otpDigits[index] && index > 0) {
      const prevInput = document.getElementById(`otp-input-${index - 1}`);
      prevInput && prevInput.focus();
    }
  };

  const validatePassword = (value) => {
    if (!value) return "Password is required.";
    if (value.length < 6) return "Password must be at least 6 characters.";
    if (!/[A-Z]/.test(value))
      return "Password must contain at least 1 uppercase letter.";
    if (!/\d/.test(value)) return "Password must contain at least 1 number.";
    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isOtpVerified) {
      toast.error("Please verify OTP before submitting.");
      return;
    }

    let hasError = false;

    if (!username || username.trim().length < 3) {
      setUsernameError("Username must be at least 3 characters.");
      hasError = true;
    } else {
      setUsernameError("");
    }

    const pwdError = validatePassword(password);
    if (pwdError) {
      setPasswordError(pwdError);
      hasError = true;
    } else {
      setPasswordError("");
    }

    if (!confirmPassword) {
      setConfirmPasswordError("Please confirm your password.");
      hasError = true;
    } else if (password && confirmPassword && password !== confirmPassword) {
      setConfirmPasswordError("Passwords and Confirm Password do not match.");
      hasError = true;
    } else {
      setConfirmPasswordError("");
    }

    const selfPwdError = validatePassword(selfPassword);
    if (selfPwdError) {
      setSelfPasswordError(selfPwdError);
      hasError = true;
    } else {
      setSelfPasswordError("");
    }

    if (hasError) return;

    const dialCode = country?.dialCode?.toString() || "";
    const mobileWithoutCode =
      phone && dialCode ? phone.toString().slice(dialCode.length) : phone;

    const payload = {
      mobile: mobileWithoutCode,
      user_name: username.trim(),
      name: username.trim(),
      password,
      self_password: selfPassword,
    };

    try {
      setLoadingSubmit(true);
      const response = await axiosInstance.post(
        "/user/registerAffiliateUser",
        payload,
      );

      if (response?.status) {
        toast.success(
          response?.msg || "Affiliate user registered successfully.",
        );
        window.location.replace("/");
      } else {
        toast.error(
          response?.msg ||
            "Failed to register affiliate user. Please try again.",
        );
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.msg ||
          err?.response?.data?.message ||
          "Failed to register affiliate user. Please try again.",
      );
    } finally {
      setLoadingSubmit(false);
    }
  };

  return (
    <div className="login-component">
      <div className="modal-header bg-transparent border-0 p-0">
        <h5 className="modal-title">Add New User</h5>
        <button
          type="button"
          className="btn-close"
          onClick={() => {
            onClose();
            resetForm();
          }}
        ></button>
      </div>
      <form className="login-form" onSubmit={handleSubmit}>
        <div className="mb-3 position-relative mt-3 " >
          <PhoneInput
            country={"us"}
            value={phone}
            onChange={(value, data) => {
              if (otpSent) return;
              setPhone(value);
              setCountry(data);
              setOtpError("");
            }}
            inputStyle={{
              width: "100%",
            }}
            containerclassName="mb-3"
            disabled={otpSent}
          />
        </div>

        {phoneError && (
          <div
            className="text-danger"
            style={{ fontSize: "11px", marginBottom: "4px", marginTop: "-8px" }}
          >
            {phoneError}
          </div>
        )}

        <button
          type="button"
          className="btn btn-thmemes d-block w-100 mb-4"
          onClick={handleSendOtp}
          disabled={!canSendOtp}
        >
          {isOtpVerified
            ? "OTP Verified"
            : loadingSend
              ? "Sending OTP..."
              : "WhatsApp OTP"}
        </button>

        {otpSent && !isOtpVerified && (
          <div className="mb-4">
            <p className="mb-2">OTP Verification</p>
            <div
              className="otp-inputs mb-3"
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: "5px",
              }}
            >
              {otpDigits.map((digit, index) => (
                <input
                  key={index}
                  id={`otp-input-${index}`}
                  type="text"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOtpInputChange(index, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(index, e)}
                  className="otp-input"
                  style={{
                    textAlign: "center",
                    fontSize: "18px",
                    padding: "8px 0",
                    width: "42px",
                  }}
                />
              ))}
            </div>

            <span
              role="button"
              onClick={() => {
                if (loadingResend || otpTimer > 0) return;
                handleResendOtp();
              }}
              style={{
                fontSize: "12px",
                cursor: loadingResend || otpTimer > 0 ? "default" : "pointer",
                opacity: loadingResend || otpTimer > 0 ? 0.6 : 1,
                display: "inline-block",
                marginTop: "20px",
                alignSelf: "flex-end",
              }}
            >
              {loadingResend
                ? "Resending..."
                : otpTimer > 0
                  ? `Resend OTP in ${otpTimer}s`
                  : "Resend OTP"}
            </span>

            <button
              type="button"
              className="btn btn-thmemes d-block w-100 mt-3"
              onClick={handleVerifyOtp}
              disabled={loadingVerify || isOtpVerified}
            >
              {loadingVerify ? "Verifying..." : "Confirm OTP"}
            </button>

            {otpError && (
              <div
                className="text-danger mt-2"
                style={{
                  fontSize: "11px",
                  marginBottom: "4px",
                  marginTop: "-8px",
                }}
              >
                {otpError}
              </div>
            )}
          </div>
        )}

        <input
          type="text"
          id="username"
          name="username"
          placeholder="Enter Username"
          className="mb-3 form-control"
          label="Username"
          value={username}
          onChange={(e) => {
            setUsername(e.target.value);
            setUsernameError("");
          }}
          disabled={!isOtpVerified}
        />
        {usernameError && (
          <div
            className="text-danger"
            style={{
              fontSize: "11px",
              marginTop: "-8px",
              marginBottom: "8px",
            }}
          >
            {usernameError}
          </div>
        )}

        <input
          type="password"
          id="password"
          name="password"
          placeholder="password"
          showPasswordToggle={true}
          className="mb-2 form-control"
          required
          label="password"
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            setPasswordError("");
          }}
          disabled={!isOtpVerified}
        />
        {passwordError && (
          <div
            className="text-danger"
            style={{
              fontSize: "11px",
              marginTop: "-8px",
              marginBottom: "8px",
            }}
          >
            {passwordError}
          </div>
        )}

        <input
          type="password"
          id="cpassword"
          name="cpassword"
          placeholder="Confirm Password"
          showPasswordToggle={true}
          className="mb-2 form-control"
          required
          label="Confirm Password"
          value={confirmPassword}
          onChange={(e) => {
            setConfirmPassword(e.target.value);
            setConfirmPasswordError("");
          }}
          disabled={!isOtpVerified}
        />
        {confirmPasswordError && (
          <div
            className="text-danger "
            style={{
              fontSize: "11px",
              marginTop: "-8px",
              marginBottom: "8px",
            }}
          >
            {confirmPasswordError}
          </div>
        )}

        <input
          type="password"
          id="selt_password"
          name="selt_password"
          placeholder="Self Password"
          showPasswordToggle={true}
          className="mb-2 form-control"
          required
          label="Self Password"
          value={selfPassword}
          onChange={(e) => {
            setSelfPassword(e.target.value);
            setSelfPasswordError("");
          }}
          disabled={!isOtpVerified}
        />
        {selfPasswordError && (
          <div
            className="text-danger"
            style={{
              fontSize: "11px",
              marginTop: "-8px",
              marginBottom: "8px",
            }}
          >
            {selfPasswordError}
          </div>
        )}

        <button
          type="submit"
          className="btn btn-thmemes d-block w-100"
          disabled={!isOtpVerified || loadingSubmit}
        >
          {loadingSubmit ? "Submitting..." : "Submit"}
        </button>
      </form>
    </div>
  );
};

export default ShareDialog;
